/**
 * Stores React hooks
 */

import type {
  CreateStoreInput,
  StoreQueryInput,
  UpdateStoreInput,
  UpdateTenantSettingsInput,
} from '@avtofix/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { storesApi } from './stores.api';

const STORES_KEY = ['stores'] as const;

export function useStore(id: string | undefined) {
  return useQuery({
    queryKey: [...STORES_KEY, 'one', id],
    queryFn: () => {
      if (id === undefined) {
        throw new Error('id talab qilinadi');
      }
      return storesApi.getOne(id);
    },
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

export function useStores(query: Partial<StoreQueryInput> = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...STORES_KEY, 'list', query],
    queryFn: () => storesApi.list(query),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStoreInput) => storesApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORES_KEY });
    },
  });
}

export function useUpdateStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateStoreInput }) =>
      storesApi.update(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORES_KEY });
    },
  });
}

export function useDeleteStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storesApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORES_KEY });
    },
  });
}

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTenantSettingsInput }) =>
      storesApi.updateSettings(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORES_KEY });
    },
  });
}

export function useRequestMarketplace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storesApi.marketplaceRequest(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORES_KEY });
    },
  });
}

export function useApproveMarketplace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storesApi.marketplaceApprove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORES_KEY });
    },
  });
}

export function useRejectMarketplace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      storesApi.marketplaceReject(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORES_KEY });
    },
  });
}

const MARKETPLACE_REQUESTS_KEY = [...STORES_KEY, 'marketplace-requests'] as const;

export function useMarketplaceRequests() {
  return useQuery({
    queryKey: MARKETPLACE_REQUESTS_KEY,
    queryFn: () => storesApi.marketplaceRequests(),
    staleTime: 30_000,
  });
}
